import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useTranslate } from 'src/i18n/useTranslate';
import { useWelcomeModal } from './providers/WelcomeModalProvider';
import { Stage, getStage } from '../helpers';
import { useAuth } from './providers/AuthProvider';
import { useApi } from 'src/hooks/useApi';
import { TutorialAutoLaunchApiType } from 'src/types/useApi';
import { useEffect, useState } from 'react';

export const InfoBanner = () => {
    const t = useTranslate();
    const { setWelcomeGruOpen } = useWelcomeModal();
    const { isPrivacyRestricted } = useAuth();
    const [showDismissModal, setShowDismissModal] = useState(false);

    const {
        makeRequest: getTutorialAutoLaunchData,
        data: tutorialAutoLaunchData,
        loading,
    }: TutorialAutoLaunchApiType = useApi({
        method: 'GET',
        url: 'user-profile/tutorial-auto-launch-indicators',
    });

    useEffect(() => {
        if (!isPrivacyRestricted()) {
            getTutorialAutoLaunchData();
        }
    }, [isPrivacyRestricted]);

    const stage = getStage();
    let stageIndicator = '';
    if (stage == Stage.TEST) {
        stageIndicator = ' Local!';
    } else if (stage == Stage.BETA) {
        stageIndicator = ' Beta!';
    } else if (stage == Stage.GAMMA) {
        stageIndicator = ' Gamma!';
    } else {
        stageIndicator = '!';
    }

    const isDismissed =
        localStorage.getItem('gru-welcome-banner-dismissed') === 'true';
    const isFirstTime =
        !isPrivacyRestricted() &&
        !loading &&
        tutorialAutoLaunchData &&
        !tutorialAutoLaunchData.firstLoginDate;

    const handleDismiss = () => {
        setShowDismissModal(true);
    };

    const confirmDismiss = () => {
        localStorage.setItem('gru-welcome-banner-dismissed', 'true');
        setShowDismissModal(false);
    };

    const cancelDismiss = () => {
        setShowDismissModal(false);
    };

    if (isDismissed) {
        return null;
    }

    return (
        <>
            <Box margin={'m'} padding={{ horizontal: 'xxxl' }}>
                <Alert
                    statusIconAriaLabel={t('aria-label-info-icon', 'Info')}
                    header={
                        isFirstTime
                            ? t(
                                  'first-time-welcome-header',
                                  'Welcome to GRU!'
                              ) + stageIndicator
                            : t('info-banner-header', 'Welcome to GRU') +
                              stageIndicator
                    }
                    dismissible={!isDismissed}
                    onDismiss={!isDismissed ? handleDismiss : undefined}
                >
                    <Box>
                        {isFirstTime
                            ? t(
                                  'first-time-welcome-content',
                                  'This is your first time using the Global Robotics University. To learn more, launch the <tutorial-link>GRU tutorial</tutorial-link>. To learn more, visit <wiki-link>the GRU wiki</wiki-link> or join the <slack-link>#gru-interest</slack-link> Slack channel.',
                                  {
                                      'tutorial-link': (chunk: string) => (
                                          <Link
                                              key="tutorial-link"
                                              href="https://broadcast.amazon.com/videos/1278680/"
                                              target="_blank"
                                          >
                                              {chunk}
                                          </Link>
                                      ),
                                      'wiki-link': (chunk: string) => (
                                          <Link
                                              key="wiki-link"
                                              href="https://w.amazon.com/bin/view/Amazon_Robotics/BOS/LExT/GRU/"
                                              target="_blank"
                                          >
                                              {chunk}
                                          </Link>
                                      ),
                                      'slack-link': (chunk: string) => (
                                          <Link
                                              key="slack-link"
                                              href="https://amazon.enterprise.slack.com/archives/C07L7GWGPKN"
                                              target="_blank"
                                          >
                                              {chunk || '#gru-interest'}
                                          </Link>
                                      ),
                                  }
                              )
                            : t(
                                  'info-banner-content',
                                  'To learn more, launch the <tutorial-link>GRU tutorial</tutorial-link>. To learn more, visit <wiki-link>the GRU wiki</wiki-link> or join the <slack-link>#gru-interest</slack-link> Slack channel.',
                                  {
                                      'tutorial-link': (chunk: string) => (
                                          <Link
                                              key="tutorial-link"
                                              href="https://broadcast.amazon.com/videos/1278680/"
                                              target="_blank"
                                          >
                                              {chunk}
                                          </Link>
                                      ),
                                      'wiki-link': (chunk: string) => (
                                          <Link
                                              key="wiki-link"
                                              href="https://w.amazon.com/bin/view/Amazon_Robotics/BOS/LExT/GRU/"
                                              target="_blank"
                                          >
                                              {chunk}
                                          </Link>
                                      ),
                                      'slack-link': (chunk: string) => (
                                          <Link
                                              key="slack-link"
                                              href="https://amazon.enterprise.slack.com/archives/C07L7GWGPKN"
                                              target="_blank"
                                          >
                                              {chunk || '#gru-interest'}
                                          </Link>
                                      ),
                                  }
                              )}
                    </Box>
                    <Box fontSize="body-s" color="text-body-secondary">
                        {t(
                            'browser-compatibility',
                            'Please note that GRU works best with Chrome or Edge, as Firefox is currently experiencing issues.'
                        )}
                    </Box>
                </Alert>
            </Box>

            <Modal
                visible={showDismissModal}
                onDismiss={cancelDismiss}
                header={t(
                    'dismiss-welcome-modal-header',
                    'Dismiss Welcome Message'
                )}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={cancelDismiss}>
                                {t('cancel-button', 'Cancel')}
                            </Button>
                            <Button variant="primary" onClick={confirmDismiss}>
                                {t('dismiss-button', 'Dismiss')}
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="m">
                    <Box>
                        {t(
                            'dismiss-welcome-modal-content',
                            'Are you sure you want to dismiss this permanently?'
                        )}
                    </Box>
                    <Box>
                        {t(
                            'dismiss-welcome-modal-reminder',
                            'The GRU Tutorial is always available at the bottom of the page or in the info bar.'
                        )}
                    </Box>
                </SpaceBetween>
            </Modal>
        </>
    );
};
